import React, { useEffect, useState } from 'react';
import { Formik, getIn } from 'formik';
import { get, isEmpty } from 'lodash';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { object, string } from 'yup';
import userImage from '../../../assets/img/placeHolderImage.png';
import config from '../../../config';
import convertJsonToFormData from '../../helpers/helper';
import {
  PHONE_NUMBER_REGEX,
  VERIFYME_TYPE,
  acceptedFileTypes,
  SUB_USER_ROLE_CLIENT_INC_COMPANY,
  SUB_USER_ROLE_CLIENT_FREEZONE_ENTITY,
  SUB_USER_ROLE_CLIENT_GOV_ENTITY,
  SUB_USER_ROLE_CLIENT_PVT_ENTITY,
} from '../../helpers/constants';
import { useVerifyme } from '../../helpers/customHooks';
import { extractUserDocs, getClientName, localNumber } from '../../helpers/utils';
import { showModal } from '../../redux/error/ErrorAction';
import { hideLoading, showLoading } from '../../redux/loader/LoaderActions';
import { getLoggedInUser } from '../../redux/user/UserActions';
import { GET, POST } from '../../services/rest.service';
import TextField from '../../components/UI/TextField';
import FIleField from '../../components/UI/FIleField';
import PhoneNumber from '../../components/global/PhoneNumber';
import OTPFormField from '../../components/global/ClientVerification/FormFields/OTP';

// The account holder's own details. For a company they are shown as the
// Authorised Representative, as on the old Corporate Details page.
const REP_FIELDS = ['first_name', 'last_name', 'dob', 'nationality', 'address'];

// The phone number cannot be changed here until SMS verification works: the
// code is sent without the country code and Twilio rejects it (F1 in
// SUGGESTIONS.md), so importers only ever saw an error. Set to false once that
// is fixed to bring back the Verify Phone Number step below.
const PHONE_LOCKED = true;

/**
 * An importer's single Profile Details page.
 *
 * Replaces Edit Profile plus Personal Details (individuals) or Corporate
 * Details and its Authorised Representative panel (companies). It keeps
 * their rules, with the changes agreed on 28 Sep 2026:
 *
 * - the phone number is verified by SMS only when it is changed, so saving
 *   anything else no longer waits on a verification (the number is locked
 *   for now, see PHONE_LOCKED);
 * - the login email is shown but cannot be changed, for companies too (the
 *   old representative panel let them change it);
 * - a company's representative details are required once any of them is
 *   filled in, so a company that never added them can still save the rest;
 * - the RC Number is sent, and compliance officers notified, only when it
 *   changes.
 */
const ProfileDetails = (props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const storeUser = useSelector((state) => state.user.user);
  const [details, setDetails] = useState(null);
  const [avatar, setAvatar] = useState(get(storeUser, 'avatar', ''));

  const { isDocVerified, setIsDocVerified, showOTPField, verifyMeData, storeVerifyMeData, sendMsgOtp, verifyDocument, verifyUserOtp } =
    useVerifyme(dispatch);

  // Re-read the signed-in user: the WhatsApp state can change from a phone at
  // any moment (a STOP reply), and the store holds what was true at sign-in.
  useEffect(() => {
    dispatch(getLoggedInUser(props.history));
  }, [dispatch, props.history]);

  const loadDetails = () => {
    if (!storeUser.uuid) return;
    dispatch(showLoading());
    GET(`/users/getDetails/${storeUser.uuid}`)
      .then(({ data }) => {
        setDetails({ user: data.user || {}, documents: extractUserDocs(data) });
        dispatch(hideLoading());
      })
      .catch((err) => {
        dispatch(showModal(err.message));
        dispatch(hideLoading());
      });
  };

  useEffect(() => {
    loadDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeUser.uuid]);

  if (!details) return null;

  const saved = details.user;
  const clientType = saved.sub_type || saved.type;
  const isCompany = clientType !== 'application_client';
  const hasRcNumber = clientType === SUB_USER_ROLE_CLIENT_INC_COMPANY;
  const hasRepAddress = clientType !== SUB_USER_ROLE_CLIENT_FREEZONE_ENTITY;
  const hasAuthLetter =
    clientType === SUB_USER_ROLE_CLIENT_GOV_ENTITY || clientType === SUB_USER_ROLE_CLIENT_PVT_ENTITY;
  const savedMobile = get(saved, 'mobile_number', '') || '';
  const savedRc = get(details, 'documents.rc_no', '') || '';
  const phoneChanged = (v) => String(v || '').trim() !== savedMobile;

  // The representative fields a company must complete once it has started.
  const repRequired = (message) =>
    string().test('rep-required', message, function (value) {
      const started = REP_FIELDS.some((k) => String(this.parent[k] || '').trim());
      return !started || !!String(value || '').trim();
    });

  const schema = object().shape({
    first_name: isCompany ? repRequired('First Name is required') : string().required('First name is required'),
    last_name: isCompany ? repRequired('Last Name is required') : string().required('Last name is required'),
    dob: isCompany ? repRequired('DOB is required') : string(),
    nationality: isCompany ? repRequired('Nationality is required') : string(),
    company_name: isCompany ? string().required('Company name is required') : string(),
    company_address: isCompany ? string().required('Address is required') : string(),
    // Registration requires it for an incorporated company.
    rc_no: hasRcNumber ? string().trim().required('RC Number is required') : string(),
    // Checked for format only when changed: a number already on file in
    // another shape ("+234...") must not stop someone saving their address.
    // Required only when changed: while the field is locked, an account with
    // no number on file must still be able to save the rest.
    mobile_number: string()
      .test('phone-required', 'Phone Number is required', (v) => !phoneChanged(v) || !!String(v || '').trim())
      .test(
        'phone-format',
        'Enter Phone Number without Country Code and without Space',
        (v) => !phoneChanged(v) || PHONE_NUMBER_REGEX.test(v),
      )
      .test(
        'no-placeholders',
        'Phone number contains invalid characters',
        (v) => !phoneChanged(v) || !/[Xx]/.test(v),
      ),
    // Blank means "use my phone number". Otherwise 0 followed by ten digits,
    // matching the API rule; the phone number shown unchanged is not checked.
    whatsapp_number: string()
      .trim()
      .test(
        'local-whatsapp-number',
        'Enter an 11-digit number starting with 0',
        (value) => !value || localNumber(value) === localNumber(savedMobile) || /^0[0-9]{10}$/.test(value),
      ),
  });

  const initialValues = {
    first_name: get(saved, 'first_name', '') || '',
    last_name: get(saved, 'last_name', '') || '',
    dob: get(saved, 'dob', '') || '',
    nationality: get(saved, 'nationality', '') || '',
    address: get(saved, 'address', '') || '',
    company_name: get(saved, 'company_name', '') || '',
    company_address: get(saved, 'company_address', '') || '',
    rc_no: savedRc,
    mobile_number: savedMobile,
    authorisation_letter: '',
    otp: '',
    // Shows the number actually messaged: the separate WhatsApp number if
    // one is set, otherwise the phone number.
    whatsapp_number: localNumber(get(saved, 'whatsapp_number', '') || savedMobile),
  };

  const onSubmit = async (values, { setSubmitting }) => {
    const payload = {
      user_uuid: saved.uuid,
      mobile_number: values.mobile_number.trim(),
      first_name: values.first_name,
      last_name: values.last_name,
      dob: values.dob,
      nationality: values.nationality,
    };
    if (!isCompany || hasRepAddress) payload.address = values.address;
    if (isCompany) {
      payload.company_name = values.company_name;
      payload.company_address = values.company_address;
    }
    // The field shows the phone number when no separate WhatsApp number is
    // set. Saving that unchanged must not store a copy of it, so a separate
    // number is kept only when it differs.
    const entered = String(values.whatsapp_number || '').trim();
    payload.whatsapp_number =
      entered && localNumber(entered) !== localNumber(savedMobile) ? entered : '';

    // Built by hand rather than with convertJsonToFormData, which drops empty
    // values: an emptied field, such as a cleared WhatsApp number, has to reach
    // the API to be cleared. Multipart so the authorisation letter can travel.
    const formData = new FormData();
    Object.keys(payload).forEach((k) => formData.append(k, payload[k] == null ? '' : payload[k]));
    if (hasAuthLetter && values.authorisation_letter) {
      formData.append('authorisation_letter', values.authorisation_letter);
    }

    dispatch(showLoading());

    // RC Number first: it is the step that can be refused ("already exists"),
    // and saving it notifies the compliance officers, so only when changed.
    const newRc = String(values.rc_no || '').trim();
    let rcSaved = false;
    if (hasRcNumber && newRc !== savedRc) {
      try {
        await POST('/users/uploadUserDocs', convertJsonToFormData({ rc_no: newRc }));
        rcSaved = true;
      } catch (err) {
        dispatch(showModal(err.message));
        dispatch(hideLoading());
        setSubmitting(false);
        return;
      }
    }

    try {
      const { data } = await POST('/users/update', formData);
      if (!isEmpty(verifyMeData)) {
        await storeVerifyMeData(saved.uuid, 'user');
      }
      dispatch(showModal('Profile updated successfully!', true));
      if (data && data.user) {
        dispatch({ type: 'USER', payload: { user: data.user, type: data.user.type } });
      }
      dispatch(getLoggedInUser(props.history));
      dispatch(hideLoading());
      history.goBack();
    } catch (err) {
      dispatch(
        showModal(rcSaved ? `Your RC Number was saved, but your other details were not: ${err.message}` : err.message),
      );
      dispatch(hideLoading());
      setSubmitting(false);
    }
  };

  const field = (renderProps, id, label, extra = {}) => {
    const { values, touched, errors, handleChange, handleBlur } = renderProps;
    return (
      <>
        <label htmlFor={id}>{label}</label>
        <TextField
          id={id}
          type="text"
          className="form-control "
          value={values[id]}
          onChange={handleChange}
          onBlur={handleBlur}
          error={getIn(touched, id) && getIn(errors, id)}
          {...extra}
        />
      </>
    );
  };

  return (
    // tabcontent places the page beside the side menu and under the header,
    // as on every other importer page.
    <div id="tabblock1" className="tabcontent">
      <section className="bodycont">
        <div>
          <h2>Profile Details</h2>
          <Formik initialValues={initialValues} validationSchema={schema} onSubmit={onSubmit}>
            {(renderProps) => {
              const { values, touched, errors, handleBlur, handleSubmit, isSubmitting, setFieldValue, setFieldTouched } =
                renderProps;
              const changed = phoneChanged(values.mobile_number);
              const awaitingVerification = changed && !isDocVerified;

              return (
                <form onSubmit={handleSubmit}>
                  <div className="personal-info">
                    <div className="profilesec">
                      <div className="profileimage">
                        <img
                          src={avatar ? `${config.api.URL.replace('/api/v1', '')}/${avatar}` : userImage}
                          alt=""
                          height="170px"
                          weight="170px"
                        />
                        <span>
                          <input
                            className="d-none"
                            id={'profile'}
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/svg"
                            name="profile"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                dispatch(showLoading());
                                POST('/users/uploadAvatar', convertJsonToFormData({ avatar: file }))
                                  .then(({ data }) => {
                                    setAvatar(data.avatar);
                                    dispatch(getLoggedInUser(props.history));
                                    dispatch(hideLoading());
                                  })
                                  .catch((err) => {
                                    dispatch(showModal(err.message));
                                    dispatch(hideLoading());
                                  });
                              }
                            }}
                          />
                          <label htmlFor="profile">
                            <span className="profile-camera">
                              <i className="fa fa-camera" aria-hidden="true"></i>
                            </span>
                          </label>
                        </span>
                      </div>
                      {getClientName(saved)}
                    </div>

                    <h3>{isCompany ? 'Company Information' : 'Account Information'}</h3>

                    <div className="row">
                      <div className="col-sm-6 text-left mb-2">
                        <label htmlFor="email">Email:</label>
                        <TextField
                          id="email"
                          type="email"
                          className="form-control "
                          value={get(saved, 'email', '')}
                          disabled={true}
                        />
                      </div>
                      <div className="col-sm-6 text-left mb-2">
                        <label htmlFor="mobile_number">Phone Number:</label>
                        {PHONE_LOCKED ? (
                          <TextField
                            id="mobile_number"
                            type="text"
                            className="form-control "
                            value={values.mobile_number}
                            disabled={true}
                          />
                        ) : (
                          <PhoneNumber
                            value={values.mobile_number}
                            onChange={(e) => {
                              setFieldValue('mobile_number', e.target.value);
                              // A new number needs a new verification.
                              setIsDocVerified(false);
                            }}
                            onBlur={handleBlur}
                            error={getIn(touched, 'mobile_number') && getIn(errors, 'mobile_number')}
                          />
                        )}
                      </div>

                      {/* Asked for only when the number has been changed. */}
                      {awaitingVerification && !showOTPField && (
                        <div className="col-sm-12 text-left mb-2 mt-2">
                          <button
                            id="verify_phone"
                            className="form-control submitbut"
                            type="button"
                            onClick={async () => {
                              const sft = await setFieldTouched('mobile_number', true);
                              if (!sft?.mobile_number) {
                                verifyDocument(VERIFYME_TYPE, {
                                  referenceId: values.mobile_number,
                                  dob: moment(values.dob).format('DD-MM-YYYY'),
                                  firstname: values.first_name,
                                  lastname: values.last_name,
                                });
                              }
                            }}
                          >
                            Verify Phone Number
                          </button>
                        </div>
                      )}
                      {changed && showOTPField && (
                        <div className="col-sm-12 text-left mb-2 mt-2">
                          <OTPFormField
                            {...renderProps}
                            phone={values.mobile_number}
                            verifyUserOtp={(phone, otp) => verifyUserOtp(phone, otp, 'user')}
                            resendOtp={sendMsgOtp}
                          />
                        </div>
                      )}

                      {isCompany ? (
                        <>
                          <div className="col-sm-6 text-left mb-2">
                            {field(renderProps, 'company_name', 'Company Name:', { placeholder: 'Company Name*' })}
                          </div>
                          <div className="col-sm-6 text-left mb-2">
                            {field(renderProps, 'company_address', 'Company Address:', {
                              placeholder: 'Company Reg. Address*',
                            })}
                          </div>
                          {hasRcNumber && (
                            <div className="col-sm-6 text-left mb-2">
                              {field(renderProps, 'rc_no', 'RC Number:', { placeholder: 'RC Number' })}
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="col-sm-6 text-left mb-2">
                            {field(renderProps, 'first_name', 'First Name:', { placeholder: 'First Name*' })}
                          </div>
                          <div className="col-sm-6 text-left mb-2">
                            {field(renderProps, 'last_name', 'Last Name:', { placeholder: 'Last Name*' })}
                          </div>
                        </>
                      )}

                      <div className="col-sm-12 text-left mt-3">
                        <hr />
                        <h3>
                          {isCompany ? 'Authorised Representative' : 'Personal Details'}
                        </h3>
                      </div>

                      {isCompany && (
                        <>
                          <div className="col-sm-6 text-left mb-2">
                            {field(renderProps, 'first_name', 'First Name:', { placeholder: 'First Name' })}
                          </div>
                          <div className="col-sm-6 text-left mb-2">
                            {field(renderProps, 'last_name', 'Last Name:', { placeholder: 'Last Name' })}
                          </div>
                        </>
                      )}
                      <div className="col-sm-6 text-left mb-2">
                        {field(renderProps, 'dob', 'Date of Birth:', {
                          type: 'date',
                          max: moment().format('YYYY-MM-DD'),
                        })}
                      </div>
                      <div className="col-sm-6 text-left mb-2">
                        {field(renderProps, 'nationality', 'Nationality:', { placeholder: 'Nationality' })}
                      </div>
                      {(!isCompany || hasRepAddress) && (
                        <div className="col-sm-12 text-left mb-2">
                          {field(renderProps, 'address', 'Residential Address:', {
                            placeholder: 'Residential Address',
                          })}
                        </div>
                      )}
                      {hasAuthLetter && (
                        <div className="col-sm-12 text-left mb-2 mt-2">
                          {saved.authorisation_letter && (
                            <div className="form-group">
                              <a
                                target="_blank"
                                rel="noopener noreferrer"
                                href={`${config.api.BASE_URL}/${saved.authorisation_letter}`}
                              >
                                <i className="fa fa-file-text-o"></i> Authorisation letter
                              </a>
                            </div>
                          )}
                          <div
                            className="fileUploads"
                            style={{ background: values.authorisation_letter ? '#01a8af' : '' }}
                          >
                            <span>
                              <i className="fa fa-file-text-o" aria-hidden="true"></i> Authorisation letter
                            </span>
                            <FIleField
                              type="file"
                              accept={acceptedFileTypes}
                              name="authorisation_letter"
                              onChange={(event) => setFieldValue('authorisation_letter', event.target.files[0])}
                              className="uploads"
                            />
                          </div>
                        </div>
                      )}

                      {/* WhatsApp. Everyone is subscribed by default; turning it
                          off is a STOP reply on WhatsApp and back on is START.
                          What a person can change here is the number. */}
                      <div className="col-sm-12 text-left mb-2 mt-3">
                        <hr />
                        <label>WhatsApp Updates:</label>
                        <p className="mb-1" id="whatsapp_status">
                          {get(storeUser, 'whatsapp_opt_in', true) !== false ? (
                            <>
                              Job updates are sent to you on WhatsApp. Reply <strong>STOP</strong> on
                              WhatsApp to turn them off.
                            </>
                          ) : (
                            <>
                              WhatsApp updates are off. Reply <strong>START</strong> on WhatsApp to
                              turn them back on.
                            </>
                          )}
                        </p>
                      </div>
                      <div className="col-sm-6 text-left mb-2">
                        <label htmlFor="whatsapp_number">Current WhatsApp Number:</label>
                        <TextField
                          id="whatsapp_number"
                          type="text"
                          className="form-control"
                          value={values.whatsapp_number}
                          maxLength={11}
                          inputMode="numeric"
                          // Digits only, at most eleven.
                          onChange={(e) =>
                            setFieldValue('whatsapp_number', e.target.value.replace(/\D/g, '').slice(0, 11))
                          }
                          onBlur={handleBlur}
                          error={getIn(touched, 'whatsapp_number') && getIn(errors, 'whatsapp_number')}
                        />
                      </div>
                    </div>

                    <div className="buttonsec">
                      <button type="button" onClick={() => history.goBack()} className="submitbut btn-light">
                        Back
                      </button>
                      <button
                        id="save_profile"
                        className="submitbut"
                        type="submit"
                        disabled={isSubmitting || awaitingVerification}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                </form>
              );
            }}
          </Formik>
        </div>
      </section>
    </div>
  );
};

export default ProfileDetails;
